// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Training Records
//
// STATIC SAMPLE DATA, generated by the same rules as the approved prototype
// (alpaca_training_demo_v26.html, 2026-08-14) so the screen shows the identical
// 24 employees and 25 records.
//
// Nothing here is persisted. The screen exists so the LAYOUT and the workflow
// can be reviewed before a schema is designed around them; see the header of
// TrainingRecordsPage.tsx for what changes when it is wired up.
//
// ⚠️ "TR-2026-00140" is a human-readable SEQUENTIAL id. When this is persisted it
// MUST be minted through ISequentialIdGenerator, never MAX(id)+1 — CLAUDE.md
// §14a Rule B. nextRecordId() below only PREVIEWS the next number.
// ─────────────────────────────────────────────────────────────────────────────

export type Category = 'DOC' | 'SOP' | 'Miscellaneous';
export type RecordStatus = 'Qualified' | 'Pending QA Review' | 'Failed';

export interface Employee { id: string; name: string }

/** The QA review + electronic signature attached to a record. */
export interface QaReview {
  date:    string;
  id:      string;
  role:    string;
  status:  '' | 'Pass' | 'Fail';
  comment: string;
  signed:  boolean;
  hash:    string;
}

/** One audit row: when, who, their role, what they did, and why. */
export type AuditRow = [date: string, user: string, role: string, action: string, meaning: string];

export interface TrainingRecord {
  id:         string;
  employee:   string;
  category:   Category;
  type:       string;
  date:       string;
  status:     RecordStatus;
  expiration: string;
  files:      string[];
  audit:      AuditRow[];
  qa:         QaReview;

  // DOC only.
  // Tests and methods are LISTS: one demonstration of capability routinely
  // qualifies an analyst on several related test codes / methods at once, so
  // forcing one row per pair would split a single DOC into many records that
  // then have to be signed, certified and expired independently.
  tests?:   string[];
  methods?: string[];
  job?:    string;
  qb?:     string;
  // SOP only
  sop?:     string;
  title?:   string;
  version?: string;
  trainer?: string;
  // Miscellaneous only
  miscType?: string;
  miscDesc?: string;

  /** Set ONCE, when the certificate is issued. Its presence is what separates a
   *  preview (watermarked, disclaimed, re-previewable) from an issued
   *  certificate (numbered, dated, and never re-issued). */
  certificateNo?: string;
  certificateIssuedOn?: string;
  certificateIssuedBy?: string;
}

export const CATEGORIES: Category[] = ['DOC', 'SOP', 'Miscellaneous'];
export const DOC_TYPES = ['IDOC', 'ODOC', 'MDL'];

/** Test and Method are CHECKED combos — the value must come from the master
 *  list, never free text, or the same analysis ends up recorded as "SM 5310B",
 *  "SM 5310 B" and "sm5310b" and no report can group them.
 *
 *  ⚠️ STATIC PLACEHOLDER. When this screen is wired up these come from the Test
 *  Code library and the Method form respectively — via the shared
 *  `ILookupReader` / lookup endpoints (§19.2 #10), NOT from a list hardcoded
 *  here. The shape (id + label) is already what the lookup returns so only the
 *  source changes. */
export const TEST_CODES = ['Metals', 'DOC', 'Alkalinity', 'UV 254'];
export const METHODS = ['EPA 200.7', 'SM 5310 B', 'SM 2320 B', 'SM 5910 B'];
export const STATUSES: RecordStatus[] = ['Qualified', 'Pending QA Review', 'Failed'];
export const PAGE_SIZES = [10, 20, 50, 100];

/** Signed-in demo identity — the prototype hard-codes this in the header. */
export const CURRENT_USER = { name: 'Demo QA Officer', role: 'QA Officer' };

export const EMPLOYEES: Employee[] = Array.from({ length: 24 }, (_, i) => ({
  id:   `E${10000 + i + 1}`,
  name: `Demo Analyst ${String(i + 1).padStart(2, '0')}`,
}));

/** Rebuilds the prototype's 25 sample records with the same rules, so the two
 *  screens show identical data and can be compared side by side during review. */
function buildRecords(): TrainingRecord[] {
  const out: TrainingRecord[] = [];
  for (let r = 1; r <= 25; r++) {
    const emp = EMPLOYEES[(r - 1) % EMPLOYEES.length]!;
    const cat = CATEGORIES[(r - 1) % 3]!;
    const type = cat === 'DOC'
      ? (r % 3 === 0 ? 'MDL' : r % 2 === 0 ? 'ODOC' : 'IDOC')
      : cat === 'SOP' ? 'SOP' : 'Safety';
    // Every 7th record is left awaiting review — without at least one the
    // Pending filter and the sign flow would have nothing to act on.
    const status: RecordStatus = r % 7 === 0 ? 'Pending QA Review' : 'Qualified';
    const day = String(28 - ((r - 1) % 20)).padStart(2, '0');
    const date = `2026-07-${day}`;

    const rec: TrainingRecord = {
      id:   `TR-2026-${String(140 - r).padStart(5, '0')}`,
      employee: emp.name,
      category: cat,
      type,
      date,
      status,
      // A qualification expires a year on; an unreviewed one has no expiry yet.
      expiration: status === 'Qualified' ? `2027-07-${day}` : '',
      files: r % 5 === 0 ? [] : [`Evidence_${r}.pdf`],
      audit: [],
      qa: { date: '', id: '', role: 'QA Officer', status: '', comment: '', signed: false, hash: '' },
    };

    if (cat === 'DOC') {
      // Every third DOC covers a second test/method pair, so the multi-select
      // case is visible in the sample data rather than only in theory.
      const i = r % 4;
      rec.tests   = r % 3 === 0 ? [TEST_CODES[i]!, TEST_CODES[(i + 1) % 4]!] : [TEST_CODES[i]!];
      rec.methods = r % 3 === 0 ? [METHODS[i]!, METHODS[(i + 1) % 4]!]       : [METHODS[i]!];
      rec.job    = `260813${String(100 + r).slice(-3)}`;
      rec.qb     = `QB${rec.job}_${String(r % 4 === 0 ? 40 : 1).padStart(3, '0')}`;
    } else if (cat === 'SOP') {
      rec.sop     = `SOP-${1000 + r}`;
      rec.title   = ['Sample Receiving', 'Data Review', 'Sample Login', 'Balance Operation'][r % 4];
      rec.version = `${2 + (r % 4)}.0`;
    } else {
      rec.miscType = 'Safety';
      rec.miscDesc = ['Chemical Hygiene', 'Annual Safety', 'PPE Training', 'Emergency Response'][r % 4];
    }

    if (status === 'Qualified') {
      rec.qa = { date: `${date} 14:30`, id: 'Demo QA Officer', role: 'QA Officer',
                 status: 'Pass', comment: 'Evidence reviewed.', signed: true, hash: '' };
      rec.audit = [
        [`${date} 10:00`, emp.name, 'Analyst', 'Created', 'Training record created'],
        [`${date} 14:30`, 'Demo QA Officer', 'QA Officer', 'QA Review', 'Pass'],
      ];
    }
    out.push(rec);
  }
  return out;
}

export const TRAINING_RECORDS: TrainingRecord[] = buildRecords();

/** The one-line subject shown in the grid — whichever field carries the meaning
 *  for that category. Without this the grid would need three columns that are
 *  each empty two-thirds of the time. */
export function subjectOf(r: TrainingRecord): string {
  if (r.category === 'DOC') {
    return [list(r.tests), list(r.methods)].filter(Boolean).join(' · ');
  }
  if (r.category === 'SOP') return [r.sop, r.title, r.version && `v${r.version}`].filter(Boolean).join(' · ');
  return [r.miscType, r.miscDesc].filter(Boolean).join(' · ');
}

/** Renders a multi-value field for display. One place, so the grid, the export,
 *  the report and the certificate can never disagree about the separator. */
export function list(values: string[] | undefined): string {
  return (values ?? []).filter(Boolean).join(', ');
}

/** The qualification as the signed report names it: the DOC type / SOP number /
 *  misc type, then the specifics. Distinct from `subjectOf`, which is the
 *  one-line grid cell. */
export function qualificationOf(r: TrainingRecord): string {
  if (r.category === 'DOC') return [r.type, list(r.tests), list(r.methods)].filter(Boolean).join(' · ');
  if (r.category === 'SOP') return [r.sop, r.title, r.version && `Version ${r.version}`].filter(Boolean).join(' · ');
  return [r.miscType, r.miscDesc].filter(Boolean).join(' · ');
}

/** The certificate's headline (what they are qualified IN) and the line under
 *  it. The headline is the METHOD for a DOC — a certificate that led with the
 *  test name would read "qualified in DOC", which says nothing. */
export function certSubject(r: TrainingRecord): { headline: string; detail: string } {
  if (r.category === 'DOC') return { headline: list(r.methods) || '—', detail: list(r.tests) };
  if (r.category === 'SOP') return { headline: r.sop ?? '—', detail: r.title ?? '' };
  return { headline: r.miscType ?? '—', detail: r.miscDesc ?? '' };
}

/** The certificate number a record WOULD be issued: TR-2026-00139 → TQ-2026-00139.
 *
 *  ⚠️ Derived from the record id here because this screen is a preview. When it
 *  is persisted this becomes a sequential id in its own right and MUST be minted
 *  through ISequentialIdGenerator (§14a Rule B) — two reviewers issuing at once
 *  must not be handed the same certificate number. */
export function certificateNoFor(r: TrainingRecord): string {
  return `TQ-${r.id.replace(/^TR-/, '')}`;
}

/** "E10001 - Demo Analyst 01" — the employee as the grid, the exports and the
 *  report name them. Two analysts can share a display name, so the id travels
 *  with it everywhere the record is read outside this screen. */
export function employeeLabel(name: string): string {
  const emp = EMPLOYEES.find(e => e.name === name);
  return emp ? `${emp.id} - ${emp.name}` : (name || '—');
}

export interface TrainingFilters {
  employee: string;
  recordId: string;
  category: string;
  status:   string;
  from:     string;
  to:       string;
  quick:    string;
}

export const EMPTY_FILTERS: TrainingFilters = {
  employee: '', recordId: '', category: '', status: '', from: '', to: '', quick: '',
};

/** Applies every filter at once.
 *
 *  Kept out of the component and exported so the combinations can be tested
 *  directly — a filter that silently drops rows is the failure mode here, and it
 *  is invisible on screen because the result still looks like a plausible list. */
export function filterRecords(rows: TrainingRecord[], f: TrainingFilters): TrainingRecord[] {
  const quick = f.quick.trim().toLowerCase();
  const rid   = f.recordId.trim().toLowerCase();
  return rows.filter(r => {
    if (f.employee && r.employee !== f.employee) return false;
    if (rid && !r.id.toLowerCase().includes(rid)) return false;
    if (f.category && r.category !== f.category) return false;
    if (f.status && r.status !== f.status) return false;
    // ISO yyyy-mm-dd compares correctly as a string, so no Date parsing — which
    // would drag timezone conversion into a pure date comparison (§14b.6).
    if (f.from && r.date < f.from) return false;
    if (f.to && r.date > f.to) return false;
    if (quick) {
      // employeeLabel carries the employee ID, so "E10007" finds that analyst's
      // records — the grid shows the id, so a search that ignored it would look
      // broken to anyone typing what is on screen.
      const hay = [r.id, employeeLabel(r.employee), r.category, r.type, subjectOf(r),
                   r.job, r.qb, r.status]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(quick)) return false;
    }
    return true;
  });
}

/** Which detail fields a category requires. Drives both the form and validation
 *  from ONE list, so a field can never be shown-but-not-validated. */
export const REQUIRED_BY_CATEGORY: Record<Category, {
  key: keyof TrainingRecord;
  label: string;
  /** True = render a CHECKED combo (multi-select) whose options come from
   *  master data — see trainingLookups.ts — instead of a free-text box. */
  lookup?: boolean;
}[]> = {
  DOC: [
    { key: 'type',    label: 'DOC Type' },
    { key: 'tests',   label: 'Test',   lookup: true },
    { key: 'methods', label: 'Method', lookup: true },
    { key: 'job',     label: 'JobID' },
    { key: 'qb',      label: 'QCBatchID' },
  ],
  SOP: [
    { key: 'sop',     label: 'SOP No.' },
    { key: 'title',   label: 'Title' },
    { key: 'version', label: 'Version' },
  ],
  Miscellaneous: [
    { key: 'miscType', label: 'Type' },
    { key: 'miscDesc', label: 'Description' },
  ],
};

/** Everything that must be filled before a record can be signed.
 *  Returns the missing LABELS so the message can name them — "Complete required
 *  fields" leaves someone hunting across three cards. */
export function missingRequired(r: TrainingRecord): string[] {
  const missing: string[] = [];
  if (!r.employee) missing.push('Employee');
  if (!r.category) missing.push('Category');
  if (!r.date)     missing.push('Date');
  for (const f of REQUIRED_BY_CATEGORY[r.category] ?? []) {
    const v = r[f.key];
    // An empty ARRAY is just as missing as an empty string — without this a
    // DOC with no test ticked would pass validation and reach a signature.
    const empty = Array.isArray(v) ? v.length === 0 : !String(v ?? '').trim();
    if (empty) missing.push(f.label);
  }
  return missing;
}

/** The exact payload the signature covers.
 *
 *  Serialised in a FIXED field order: a hash over an object whose key order can
 *  vary would change without the record changing, and "Verify" would then report
 *  tampering on an untouched record. */
export function signedPayload(r: TrainingRecord): string {
  return JSON.stringify({
    record: r.id, employee: r.employee, category: r.category, date: r.date,
    status: r.status, expiration: r.expiration, type: r.type,
    // Multi-value fields are serialised in their stored order. The combo keeps
    // that order stable (option order, not click order), so re-ticking the same
    // set produces the same hash and Verify does not cry tampering.
    tests: r.tests ?? [], methods: r.methods ?? [], job: r.job ?? '', qb: r.qb ?? '',
    sop: r.sop ?? '', title: r.title ?? '', version: r.version ?? '', trainer: r.trainer ?? '',
    miscType: r.miscType ?? '', miscDesc: r.miscDesc ?? '',
    files: r.files, qaStatus: r.qa.status, qaComment: r.qa.comment,
  });
}

/** SHA-256 of the signed payload, via the browser's own crypto.
 *  Falls back to a clearly-labelled marker when Web Crypto is unavailable
 *  (non-HTTPS origin, old browser) rather than inventing a fake hash — a
 *  signature nobody can verify must not look like one that can be. */
export async function sha256(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return 'unavailable-no-webcrypto';
  const buf = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Preview of the id a new record WOULD get.
 *  Preview only — the real one must come from ISequentialIdGenerator so two
 *  people creating a record at once cannot both be handed the same number. */
export function nextRecordId(rows: TrainingRecord[]): string {
  const highest = rows.reduce((max, r) => {
    const n = parseInt(r.id.split('-').at(-1) ?? '', 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `TR-2026-${String(highest + 1).padStart(5, '0')}`;
}

/** A blank record for the New action. */
export function blankRecord(rows: TrainingRecord[]): TrainingRecord {
  return {
    id: nextRecordId(rows), employee: '', category: 'DOC', type: 'IDOC',
    date: '', status: 'Pending QA Review', expiration: '', files: [], audit: [],
    qa: { date: '', id: '', role: CURRENT_USER.role, status: '', comment: '', signed: false, hash: '' },
  };
}

/** Local wall-clock stamp for audit rows, matching the prototype's format.
 *  Built from date PARTS rather than toLocaleString so a 12-hour "PM" can never
 *  appear — the app is 24-hour everywhere (§14b.6). */
export function stamp(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
